import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, BellRing } from "lucide-react";
import {
  isPushSupported, getPushPermission, getCurrentSubscription,
  enablePush, disablePush,
} from "@/lib/push";

/**
 * Toggle for browser Web Push notifications.
 * - Shows current state (unsupported / blocked / off / on).
 * - Click to enable → asks permission, registers SW, subscribes to Push service,
 *   POSTs subscription to /api/push/subscribe.
 * - Click when on → unsubscribes and removes from backend.
 */
export default function PushToggle() {
  const [state, setState] = useState("checking"); // checking|unsupported|blocked|off|on
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    if (!isPushSupported()) { setState("unsupported"); return; }
    const perm = await getPushPermission();
    if (perm === "denied") { setState("blocked"); return; }
    const sub = await getCurrentSubscription();
    setState(sub ? "on" : "off");
  };

  useEffect(() => { refresh(); }, []);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (state === "on") {
        await disablePush();
        toast.success("Push notifications dimatikan.");
      } else {
        await enablePush();
        toast.success("Push notifications aktif! Anda akan dapat alert bila signal baru dihantar.");
      }
      await refresh();
    } catch (e) {
      toast.error(e.message || "Gagal update push settings.");
    } finally { setBusy(false); }
  };

  const label = {
    checking:   { title: "Checking…",          hint: "",                                                    Icon: Bell,     btn: "…" },
    unsupported:{ title: "Not supported",      hint: "Browser anda tak sokong Web Push. Cuba Chrome/Edge/Firefox.", Icon: BellOff, btn: null },
    blocked:    { title: "Blocked",            hint: "Notification blocked di browser settings. Sila enable manual.", Icon: BellOff, btn: null },
    off:        { title: "Off",                hint: "Enable untuk terima signal alert walaupun tab ditutup.", Icon: Bell,     btn: "Enable" },
    on:         { title: "On",                 hint: "Anda akan terima push notification bila admin broadcast signal baru.", Icon: BellRing, btn: "Disable" },
  }[state];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6" data-testid="push-toggle">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-4 min-w-0">
          <div className={`w-10 h-10 rounded-lg grid place-items-center ${state === "on" ? "bg-[#00b4ff]/10 text-[#00b4ff]" : "bg-slate-100 text-slate-500"}`}>
            <label.Icon size={18} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-lg tracking-tight">Push Notifications</div>
              <span
                data-testid="push-state"
                className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-mono-plex tracking-widest uppercase ${
                  state === "on" ? "bg-emerald-50 text-emerald-700" :
                  state === "off" ? "bg-slate-100 text-slate-600" :
                  "bg-rose-50 text-rose-700"
                }`}
              >
                {label.title}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-600 max-w-md">{label.hint}</p>
          </div>
        </div>
        {label.btn && (
          <button
            data-testid="push-toggle-btn"
            onClick={toggle}
            disabled={busy || state === "checking"}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-mono-plex tracking-widest uppercase disabled:opacity-60 ${
              state === "on"
                ? "border border-slate-200 hover:bg-slate-50 text-slate-700"
                : "bg-[#00b4ff] hover:bg-[#0090cc] text-white"
            }`}
            style={{ transition: "background-color 0.2s ease" }}
          >
            {busy ? "…" : label.btn}
          </button>
        )}
      </div>
    </div>
  );
}
